import { ProfileRoles } from '@bp/pim-auth-constants';
import { ModalBottomButtons, Select, type SelectOptionType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { getProfileRoleTranslation } from 'utils/profileRoles';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';

const BpChangeProfileRoleMutationDocument = graphql(`
  mutation UpdateProfileRole($uuid: String!, $role: String!) {
    updateProfileRole(uuid: $uuid, role: $role) {
      entitiesUpdated
    }
  }
`);

const PersonProfileRoleFormQuery = graphql(`
  query PersonProfileRoleFormData($organizationUuid: ID!, $personUuid: ID) {
    profiles(where: { organization: { uuid: $organizationUuid }, uuid: $personUuid }) {
      __typename
      uuid
      organizationRoles
    }
  }
`);

type PersonProfileRoleFormProps = {
  personUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

type PersonProfileRoleFormType = {
  role: string;
};

export const PersonProfileRoleForm = ({ personUuid, onLoad, onClose }: PersonProfileRoleFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [, updateProfileRoles] = useMutation(BpChangeProfileRoleMutationDocument);

  const [{ data }] = useQuery({
    query: PersonProfileRoleFormQuery,
    variables: { organizationUuid, personUuid },
  });

  const profile = data?.profiles[0] ?? null;

  const initialValues: PersonProfileRoleFormType = {
    role:
      profile?.organizationRoles.find((r) => Object.values(ProfileRoles).includes((r as ProfileRoles) ?? '')) ??
      ProfileRoles.Other,
  };

  async function handleSubmit(values: PersonProfileRoleFormType, helper: FormikHelpers<PersonProfileRoleFormType>) {
    onLoad(true);

    const { error } = await updateProfileRoles({
      role: values.role,
      uuid: personUuid,
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    helper.resetForm();
    onClose();
  }

  const profileRoleSelectOpts = useMemo(() => {
    return Object.values(ProfileRoles).map(
      (r) => ({ label: getProfileRoleTranslation(r), value: r }) as SelectOptionType,
    );
  }, []);

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues}>
      {({ resetForm, dirty, setFieldValue, values }) => {
        return (
          <Form>
            <Select
              name='role'
              label={t('profiles.role')}
              value={profileRoleSelectOpts.find((v) => v.value === values.role)}
              options={profileRoleSelectOpts}
              onChange={(opt) => setFieldValue('role', (opt as SelectOptionType).value)}
              isSearchable
              menuPosition='fixed'
            />

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{
                disabled: !dirty,
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
